import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { updatePpwrEmPackagingTypeRequest } from 'redux/reducers/ppwr/reducer';
import { TPpwrEmPackagingType } from 'redux/reducers/ppwr/types';
import { PpwrEditValueDialog } from 'components/Ppwr/PpwrEditValueDialog';

type TEditWeightDialogProps = {
	rows: TPpwrEmPackagingType[];
	/** Index (within `rows`) of the edited row, `null` when the dialog is closed. */
	index: number | null;
	setIndex: (index: number) => void;
	onClose: () => void;
};

/** Empty clears the weight, otherwise a non-negative number (decimals allowed). */
const isValidWeight = (value: string) =>
	value.trim() === '' || /^\d+([.,]\d+)?$/.test(value.trim());

const toWeight = (value: string) => (value === '' ? null : Number(value.replace(',', '.')));

const toInitialValue = (value: number | null | undefined) =>
	value !== null && value !== undefined ? String(value) : '';

export const EditWeightDialog: FC<TEditWeightDialogProps> = ({
	rows,
	index,
	setIndex,
	onClose,
}) => {
	const dispatch = useDispatch();
	const row = index !== null ? (rows[index] ?? null) : null;

	const handleSave = ([weight, weightProductPack]: string[]) => {
		if (!row) return;
		dispatch(
			updatePpwrEmPackagingTypeRequest({
				emId: row.emId,
				pckgTypeId: row.pckgTypeId,
				// Only the weights are edited here, `enabled` is sent as it is.
				body: {
					weight: toWeight(weight),
					weightProductPack: toWeight(weightProductPack),
					enabled: row.enabled,
				},
			}),
		);
	};

	return (
		<PpwrEditValueDialog
			rowsCount={rows.length}
			index={row ? index : null}
			setIndex={setIndex}
			onClose={onClose}
			title={row?.pckgTypeName ?? `#${row?.pckgTypeId ?? ''}`}
			fields={[
				{
					label: 'Weight, grams',
					initialValue: toInitialValue(row?.weight),
					isValid: isValidWeight,
					invalidText: 'Enter a number, 0 or more',
				},
				{
					label: 'Product Pack Weight, grams',
					initialValue: toInitialValue(row?.weightProductPack),
					isValid: isValidWeight,
					invalidText: 'Enter a number, 0 or more',
				},
			]}
			onSave={handleSave}
		/>
	);
};
